/**
 * Embedded web console mode (M2-CONSOLE, architecture N9 / N10).
 *
 * The native Command Bridge app shows every module it has not built natively
 * as this React console inside a WebView. It signs the console in with a
 * single-use handoff code (route /handoff, pages/ConsoleHandoff.tsx), and that
 * handoff is the ONLY thing that turns embedded mode on: never the user agent,
 * which any browser can fake and which a real WebView may not change.
 *
 * Embedded mode:
 *   - hides chrome the native shell already provides (bottom tab bar, brand,
 *     the desktop status bar and persistent sidebar): the console always uses
 *     its compact header + drawer, at every width (getShellChrome);
 *   - tells the native shell when the console session ends or the person asks
 *     to close it, through ONE minimal message channel (postToNativeShell).
 *
 * The flag lives in sessionStorage: it is per WebView instance and dies with
 * it, so a normal browser tab is never left in embedded mode.
 */

const EMBEDDED_KEY = 'cb.console.embedded';

/** The only messages the console ever sends to the native shell. */
export type NativeShellMessage = 'close' | 'session_ended';

/**
 * Reserved scheme the native shell intercepts (and cancels) on Android and
 * iOS, where no postMessage channel is installed. Kept in step with
 * native/CommandBridge.Core/EmbeddedConsole/ConsoleShellMessage.cs.
 */
export const NATIVE_SHELL_SCHEME = 'commandbridge-console';

/** Envelope `source` for WebView2's postMessage (Windows). */
export const NATIVE_SHELL_SOURCE = 'command-bridge-console';

const listeners = new Set<() => void>();
let memoryFlag = false;

function readFlag(): boolean {
  try {
    return window.sessionStorage.getItem(EMBEDDED_KEY) === '1';
  } catch {
    return memoryFlag;
  }
}

/** Whether this console was opened by the native app's handoff. */
export function isEmbeddedMode(): boolean {
  if (typeof window === 'undefined') return false;
  return memoryFlag || readFlag();
}

/** Mark the document so CSS can react (data-embedded="true" on <html>). */
export function applyEmbeddedDocumentFlag(): void {
  if (typeof document === 'undefined') return;
  if (isEmbeddedMode()) {
    document.documentElement.dataset.embedded = 'true';
  } else {
    delete document.documentElement.dataset.embedded;
  }
}

/** Turn embedded mode on. Called by the /handoff route only. */
export function enableEmbeddedMode(): void {
  memoryFlag = true;
  try {
    window.sessionStorage.setItem(EMBEDDED_KEY, '1');
  } catch {
    // Private mode / blocked storage: the in-memory flag still holds for this page.
  }
  applyEmbeddedDocumentFlag();
  listeners.forEach((listener) => listener());
}

/** Test helper: forget embedded mode. */
export function resetEmbeddedModeForTests(): void {
  memoryFlag = false;
  try {
    window.sessionStorage.removeItem(EMBEDDED_KEY);
  } catch {
    // ignore
  }
  applyEmbeddedDocumentFlag();
  listeners.forEach((listener) => listener());
}

export function subscribeEmbeddedMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Send a message to the native shell. Windows (WebView2) has a built-in
 * postMessage channel; elsewhere the console navigates to the reserved scheme,
 * which the shell intercepts and cancels. No other bridge exists: the WebView
 * exposes no native objects to this page. Returns false outside embedded mode.
 */
export function postToNativeShell(type: NativeShellMessage): boolean {
  if (!isEmbeddedMode() || typeof window === 'undefined') return false;
  const webview = (window as unknown as { chrome?: { webview?: { postMessage?: (m: string) => void } } }).chrome?.webview;
  if (webview && typeof webview.postMessage === 'function') {
    webview.postMessage(JSON.stringify({ source: NATIVE_SHELL_SOURCE, type }));
    return true;
  }
  window.location.assign(`${NATIVE_SHELL_SCHEME}://message/${type}`);
  return true;
}

// ---------------------------------------------------------------- shell chrome

export interface ShellChrome {
  /** Persistent desktop sidebar (lg and up). */
  desktopSidebar: boolean;
  /** Desktop status bar (clock, connection, account menu). */
  desktopTopBar: boolean;
  /** Compact header with the drawer button. */
  compactHeader: boolean;
  /** Logo + organization name in the compact header. */
  brand: boolean;
  /** Fixed bottom tab bar. */
  bottomNav: boolean;
  /** Breadcrumb trail. */
  breadcrumb: boolean;
  /** Main content offset for the persistent sidebar. */
  sidebarOffset: boolean;
}

/**
 * Which pieces of the app shell to render. Outside embedded mode this is the
 * existing responsive shell. Embedded, the native shell already shows the
 * product, the navigation between native modules and the device status, so
 * the console keeps only its compact header and drawer at every width: no
 * duplicate tab bar, brand, status bar or second sidebar, and the whole
 * window width goes to the page (usable from 360 px up).
 */
export function getShellChrome(input: { embedded: boolean; isDesktop: boolean }): ShellChrome {
  if (input.embedded) {
    return {
      desktopSidebar: false,
      desktopTopBar: false,
      compactHeader: true,
      brand: false,
      bottomNav: false,
      breadcrumb: false,
      sidebarOffset: false,
    };
  }
  return {
    desktopSidebar: true,
    desktopTopBar: input.isDesktop,
    compactHeader: !input.isDesktop,
    brand: true,
    bottomNav: true,
    breadcrumb: true,
    sidebarOffset: true,
  };
}

// ---------------------------------------------------------- shell handoff nonce

/**
 * The function the native shell calls INSIDE this page to hand over the shell
 * nonce for the handoff code in the fragment. The shell evaluates
 *   window.__commandBridgeShellHandoff({ nonce: "...", replace: false })
 * in the page it loaded (MAUI WebView.EvaluateJavaScriptAsync: WebView2
 * ExecuteScriptAsync on Windows, evaluateJavascript on Android, WKWebView on
 * iOS) and retries until it answers "accepted". Kept in step with
 * native/CommandBridge.Core/EmbeddedConsole/ShellHandoff.cs.
 *
 * Why this channel: an ordinary browser has no shell to call it, and another
 * site cannot call a function inside this origin's page (unlike postMessage,
 * which any opener or framing page can send). So a handoff link opened in a
 * normal browser never gets a nonce and is never redeemed: the server binds
 * each code to sha256(nonce) at issue and refuses a redeem without it.
 */
export const SHELL_HANDOFF_GLOBAL = '__commandBridgeShellHandoff';

/** How long /handoff waits for the shell before failing closed. */
export const SHELL_HANDOFF_TIMEOUT_MS = 10_000;

/** 32 random bytes, base64url (43 characters). */
const SHELL_NONCE_RE = /^[A-Za-z0-9_-]{43}$/;

export interface ShellHandoff {
  nonce: string;
  /**
   * The shell asks to replace a console session already stored in this
   * WebView (it could not clear the WebView's storage first, or the person
   * pressed Reload). Without it, /handoff never overwrites an existing session.
   */
  replace: boolean;
}

/** Validates what the shell passed; anything else is ignored. */
export function parseShellHandoff(payload: unknown): ShellHandoff | null {
  if (!payload || typeof payload !== 'object') return null;
  const { nonce, replace } = payload as { nonce?: unknown; replace?: unknown };
  if (typeof nonce !== 'string' || !SHELL_NONCE_RE.test(nonce)) return null;
  return { nonce, replace: replace === true };
}

/**
 * Wait (at most `timeoutMs`) for the native shell to hand over the nonce.
 * Resolves null on timeout: the page is not running inside the app. The
 * receiver accepts ONE valid payload and then removes itself.
 */
export function receiveShellHandoff(timeoutMs: number = SHELL_HANDOFF_TIMEOUT_MS): Promise<ShellHandoff | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const target = window as unknown as Record<string, unknown>;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: ShellHandoff | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      delete target[SHELL_HANDOFF_GLOBAL];
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    target[SHELL_HANDOFF_GLOBAL] = (payload: unknown): string => {
      if (settled) return 'closed';
      const handoff = parseShellHandoff(payload);
      if (!handoff) return 'invalid';
      finish(handoff);
      return 'accepted';
    };
  });
}

// ---------------------------------------------------------------- handoff link

/** A handoff code: 32 random bytes, base64url (43 characters). */
const HANDOFF_CODE_RE = /^[A-Za-z0-9_-]{43}$/;

/**
 * Read the handoff code from a URL fragment ("#code=..."). The code travels in
 * the fragment, never the query string, so it is never sent to a server in a
 * request line, never written to an access log and never leaks in a Referer.
 */
export function parseHandoffFragment(hash: string | null | undefined): string | null {
  if (!hash) return null;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const code = params.get('code');
  return code && HANDOFF_CODE_RE.test(code) ? code : null;
}

/**
 * The in-app path to open after a handoff. Same rule as the server
 * (console-handoff.types.ts consoleReturnPathSchema): one leading "/", no
 * "//host", no backslash, no whitespace or control characters, never /handoff.
 */
export function safeInAppPath(path: unknown): string {
  if (typeof path !== 'string' || path.length === 0 || path.length > 512) return '/';
  // eslint-disable-next-line no-control-regex
  if (!/^\/(?![/\\])[^\s\\]*$/.test(path) || /[\u0000-\u001f\u007f]/.test(path)) return '/';
  if (/^\/handoff(?:[/?#]|$)/i.test(path)) return '/';
  return path;
}
